import {FC, useMemo} from 'react';
import {useParams} from 'wouter';

import {useResponsive} from '@shared_web/breakpoints';
import {Flex} from '@shared_web/components/core2/flex';

import {DesktopTemplate} from '@mavismart_frontend/pages/desktop_template';
import {DoorTabs} from '@mavismart_frontend/pages/door_tabs';
import {GroupTabs} from '@mavismart_frontend/pages/group_tabs';
import {AllDoorsList} from '@mavismart_frontend/pages/tabs/all_doors_list';
import {DoorsList} from '@mavismart_frontend/pages/tabs/doors_list';
import {GroupsList} from '@mavismart_frontend/pages/tabs/groups_list';
import {useGroups} from '@mavismart_frontend/stores/groups_store';

// Unified desktop page that keeps side lists mounted across route changes to preserve scroll
export const DesktopHome: FC = () => {
  const {isDesktop} = useResponsive();
  const {groupId, doorId} = useParams<{groupId?: string; doorId?: string}>();

  const fullGroups = useGroups();
  const doorIds = groupId ? fullGroups?.find(g => g.group.id === groupId)?.doorIds : undefined;

  // Side columns
  const groupsList = useMemo(() => <GroupsList key="groups" />, []);
  const doorsList = useMemo(
    () => (groupId ? <DoorsList key="doors" doorIds={doorIds} /> : <AllDoorsList key="doors" />),
    [doorIds, groupId]
  );
  const columns = useMemo(() => {
    if (!isDesktop && doorId) {
      return [doorsList];
    }
    return [groupsList, doorsList];
  }, [groupsList, doorsList, isDesktop, doorId]);

  const columnsWithWrappers = useMemo(() => {
    return columns.map(column => (
      <Flex key={column.key} $column $width="100%" $padding={10} $gap={10}>
        {column}
      </Flex>
    ));
  }, [columns]);

  // Content of the page
  const content = useMemo(() => {
    if (doorId) {
      return (
        <DoorTabs
          doorId={doorId}
          baseUrl={groupId ? `/groups/${groupId}/doors/${doorId}` : `/doors/${doorId}`}
        />
      );
    }
    if (groupId) {
      return <GroupTabs />;
    }
    return <></>;
  }, [doorId, groupId]);

  // Render the page
  return <DesktopTemplate columns={columnsWithWrappers} content={content} />;
};

DesktopHome.displayName = 'DesktopHome';
